import { VerticalOption } from '@amzn/aet-galactus-kit';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Responsive from '@amzn/meridian/responsive';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router';

import DataSource from '../../../../api';
import { StateContext } from '../../../../GlobalStateStore';
import {
  EmptyProfile,
  EmptyTeam,
  Profile,
  Team as TeamType,
  User,
  WeeklySummaryCoachingStatus,
} from '../../../../Types';
import DataState from '../../../../Types/DataState';
import {
  EmptyProxyRelatedUser,
  ProxyRelatedUser,
} from '../../../../Types/ProxyUser';
import {
  getCurrentWeek,
  weeksList,
  getDateRangeFromWeek,
} from '../../../../Util';
import {
  Feature,
  getAlertContentFromConfig,
  getNotificationContentFromConfig,
  isFeatureEnabled,
} from '../../../../config/FeaturesConfig';
import {
  EPOCH_START,
  FILTER_BAR_ID,
  FILTER_BAR_HEIGHT_THRESHOLD,
} from '../../../../Util/GlobalConstants';
import useCustomTracking from '../../../../Util/useCustomTracking';
import {
  EmptyFilters,
  EmptyLighthouse,
  TeamTableSortMethod,
  isValidDateRange,
  getDefaultDateRange,
  refreshFilters,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';
import {
  LighthouseFilters,
  LighthouseMetrics,
  TimeOption,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/types';
import UserBadge from '../../../Dashboard/DashboardComponents/UserBadge';
import CustomerComments from '../../../Dashboard/DashboardTables/CustomerComments';
import DirectReports from '../../../Team/Table/LighthouseMetrics/DirectReports';
import Summary from '../../../Team/Table/LighthouseMetrics/Summary';
import TeamProfileContainer from '../../../Team/Table/LighthouseMetrics/TeamProfileContainer';
import TeamPermissions from '../../../Team/TeamPermissions';
import Sort from '../../../Team/Table/LighthouseMetrics/LighthouseSort';
import Page from '../../../../Types/Page';
import LighthouseFilterBy from '../../../Team/Table/LighthouseMetrics/LighthouseFilterBy';
import Filter from '../../../Team/FilterComponent/Filter';
import {
  convertToVerticalOption,
  decideVerticalOption,
} from '../../../../Util/Vertical';
import { checkGoUser } from '../../../../Util/User';

const TRAILING_DAYS = 'Trailing 7 days';
const SYNC_METRICS_DEFAULT = false;

const ProxyTeam: React.FC = (): React.ReactElement => {
  const apiNaws = DataSource.getDataFeed('NAWS');
  const legacyExperience = !isFeatureEnabled(Feature.LIGHTHOUSE);
  const newCoachingEnabled = isFeatureEnabled(Feature.NEW_COACHING);
  const enableProfanityFilter = isFeatureEnabled(Feature.PROFANITY_FILTER);
  const enableRAPRemovalAndCCXUpdate = isFeatureEnabled(
    Feature.ENABLE_RAP_REMOVAL_AND_CCX_UPDATE
  );

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { userID, cscEntityPrefix, enableCesComments, manageCoachingNotes } =
    inspireOptions.data;

  const routeParams = useParams();

  const teamNotifContent = getNotificationContentFromConfig(
    Feature.TEAM_NOTIFICATION
  );

  const teamAlertContent = getAlertContentFromConfig(Feature.TEAM_ALERT);
  const [sortingMethod, setSortingMethod] = useState(
    TeamTableSortMethod.METRIC_VALUE
  );

  const [team, setTeam] = useState<TeamType>(EmptyTeam);
  const [teamState, setTeamState] = useState(DataState.LOADING);

  // @FIXME: use global state
  const [profile, setProfile] = useState<Profile>(EmptyProfile);
  const [profileState, setProfileState] = useState(DataState.LOADING);

  const [selectedAssociate, setSelectedAssociate] = useState('');
  const [associateList, setAssociateList] = useState<User[]>([]);

  const [customerComments, setCustomerComments] = useState([]);
  const [customerCommentsState, setCustomerCommentsState] = useState(
    DataState.LOADING
  );

  const teamNotificationEnabled = isFeatureEnabled(Feature.TEAM_NOTIFICATION);
  const teamAlertEnabled = isFeatureEnabled(Feature.TEAM_ALERT);
  const achtEnabled = isFeatureEnabled(Feature.ACHT_FLAG);
  const concessionEnabled = isFeatureEnabled(Feature.CONCESSION_RATE_METRIC);

  //Weekly Coaching Status Initialization
  const [weeklySummaryCoachingStatus, setWeeklySummaryCoachingStatus] =
    useState<WeeklySummaryCoachingStatus[]>([]);
  const [
    weeklySummaryCoachingStatusLoadingState,
    setWeeklySummaryCoachingStatusLoadingState,
  ] = useState(DataState.LOADING);

  // Default Vertical
  const [LHDefaultVertical, setLHDefaultVertical] = useState('');
  const [LHDefaultVerticalState, setLHDefaultVerticalState] = useState(
    DataState.LOADING
  );

  // Lighthouse Filters
  const [lighthouseFilters, setLighthouseFilters] =
    useState<LighthouseFilters>(EmptyFilters);
  const [lighthouseFiltersState, setLighthouseFiltersState] = useState(
    DataState.LOADING
  );

  // Check for Vertical is in Vertical Options or not
  const [checkOptions, setCheckOptions] = useState(false);
  const [checkState, setCheckState] = useState(DataState.LOADING);

  //Proxy Team TM Filter Selection
  const [selectedProxyCoachee, setSelectedProxyCoachee] = useState('');
  const [proxyCoachees, setProxyCoachees] = useState<ProxyRelatedUser[]>([]);
  const [selectedProxyCoacheeObject, setSelectedProxyCoacheeObject] =
    useState<ProxyRelatedUser>(EmptyProxyRelatedUser);

  // Team Filter
  const [verticalValue, setVerticalValue] = useState('');
  const [filterVerticalValue, setFilterVerticalValue] = useState('');
  const weekOptions = [TRAILING_DAYS, ...weeksList()];
  const [weekValue, setWeekValue] = useState(weekOptions[0]);
  const [skillValue, setSkillValue] = useState('ALL');
  const [channelValue, setChannelValue] = useState('ALL');
  const [segmentValue, setSegmentValue] = useState('ALL');
  const [marketplaceValue, setMarketplaceValue] = useState('ALL');
  const [skillOptions, setSkillOptions] = useState<string[]>([]);
  const [segmentOptions, setSegmentOptions] = useState<string[]>([]);
  const [verticalOptions, setVerticalOptions] = useState<string[]>([]);
  const [channelOptions, setChannelOptions] = useState<string[]>([]);
  const [marketplaceOptions, setMarketplaceOptions] = useState<string[]>([]);

  // Custom Date Feature: defualt set to recent 7 days
  const [customDate, setCustomDate] = useState(false);
  const defaultDateRange = getDefaultDateRange();
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [startDate, setStartDate] = useState(defaultDateRange[0]);
  const [endDate, setEndDate] = useState(defaultDateRange[1]);
  const [shortFilterBy, setShortFilterBy] = useState(false);

  // Summary Table - Lighthouse Metrics
  const [lighthouseMetrics, setLighthouseMetrics] =
    useState<LighthouseMetrics>(EmptyLighthouse);
  const [lighthouseMetricsState, setLighthouseMetricsState] = useState(
    DataState.LOADING
  );

  // contact listening
  const [contactHandlerId, setContactHandlerId] = useState('');

  // DirectReports Table - Team Lighthouse Metrics
  const [teamLHMetrics, setTeamLHMetrics] = useState<LighthouseMetrics[]>([]);
  const [teamLHMetricsState, setTeamLHMetricsState] = useState(
    DataState.LOADING
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [latestMetricPublishDate, setLatestMetricPublishDate] =
    useState(EPOCH_START);
  const [syncTimeChangeCount, setSyncTimeChangeCount] = useState(0);

  const updateTeamWithProfile = () => {
    // Collect Keys/Permissions from InspireOptions
    const availableKeys: string[] = [];
    if (inspireOptions.data.manageCoachingNotes) {
      availableKeys.push(TeamPermissions.coachingNotes);
    }

    const teamCopy = new TeamType({
      keys: availableKeys,
      metrics: [],
      summaryMetrics: [],
    });

    // @toDo - This type need to refacot - type is out of dated
    setTeam(teamCopy);
  };

  useEffect(() => {
    if (
      lighthouseFiltersState !== DataState.LOADED &&
      lighthouseMetricsState !== DataState.LOADED &&
      LHDefaultVerticalState !== DataState.LOADED &&
      teamLHMetricsState !== DataState.LOADED &&
      weeklySummaryCoachingStatusLoadingState !== DataState.LOADED
    )
      setTeamState(DataState.LOADED);
  }, [
    lighthouseFiltersState,
    lighthouseMetricsState,
    teamLHMetricsState,
    LHDefaultVerticalState,
    weeklySummaryCoachingStatusLoadingState,
  ]);

  const filterTruncate = {
    '500px': '100%',
    '860px': '50%',
    '1240px': '25%',
  };

  const isGoUser = checkGoUser(profile.user.owner);

  useEffect(() => {
    if (filterVerticalValue === '') return;
    setVerticalValue(
      decideVerticalOption(
        filterVerticalValue || LHDefaultVertical,
        isGoUser,
        enableRAPRemovalAndCCXUpdate
      )
    );
  }, [
    filterVerticalValue,
    isGoUser,
    LHDefaultVertical,
    enableRAPRemovalAndCCXUpdate,
  ]);

  useEffect(() => {
    // update args used for qureying if and only if the selected date range is valid
    if (isValidDateRange(dateRange[0], dateRange[1])) {
      setStartDate(dateRange[0]);
      setEndDate(dateRange[1]);
    }
  }, [dateRange]);

  useEffect(() => {
    // reset all filters (except vertical) to default every time user toggles the custom date switch
    if (customDate) {
      setDateRange(defaultDateRange);
      setStartDate(defaultDateRange[0]);
      setEndDate(defaultDateRange[1]);
    } else {
      setWeekValue(weekOptions[0]);
    }
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
  }, [customDate]);

  // GetProfileData
  useEffect(() => {
    if (inspireOptions.state === DataState.LOADED) {
      const userId = routeParams.userId ? routeParams.userId : userID;
      const queryVariables = apiNaws.prepareQuery(
        weekValue === TRAILING_DAYS ? getCurrentWeek() : weekValue,
        userId,
        cscEntityPrefix
      );
      apiNaws.getProfileData(queryVariables, setProfile, setProfileState);
    }
  }, [inspireOptions.state]);

  useEffect(() => {
    if (profileState !== DataState.LOADED) return;
    updateTeamWithProfile();
  }, [profileState]);

  const sortCoachees = (a: ProxyRelatedUser, b: ProxyRelatedUser) => {
    if (a.tmEntity.displayName < b.tmEntity.displayName) return -1;
    if (a.tmEntity.displayName > b.tmEntity.displayName) return 1;
    return 0;
  };

  //First loaded and intialize the data
  useEffect(() => {
    if (profile.user.userID === '' || profileState === DataState.LOADING)
      return;

    const proxyCoachees = profile.proxyCoachees
      .filter(proxyCoachee => proxyCoachee.sharedReports.length)
      .sort(sortCoachees);

    setProxyCoachees(proxyCoachees);
    setSelectedProxyCoachee(proxyCoachees[0]?.tmEntity.userID ?? '');
    setSelectedProxyCoacheeObject(proxyCoachees[0]);
  }, [profile.user.userID, profileState]);

  useEffect(() => {
    if (
      selectedProxyCoacheeObject.tmEntity.userID === '' ||
      !selectedProxyCoacheeObject.sharedReports.length
    )
      return;

    setAssociateList(selectedProxyCoacheeObject.sharedReports);
    setSelectedAssociate(selectedProxyCoacheeObject.sharedReports[0].userID);
  }, [selectedProxyCoachee, selectedProxyCoacheeObject]);

  // Default Vertical Integration
  useEffect(() => {
    if (selectedProxyCoachee === '' || profileState === DataState.LOADING) {
      return;
    }
    const queryVariables = apiNaws.prepareLighthouseDefaultVerticalQuery(
      selectedProxyCoachee,
      cscEntityPrefix
    );
    apiNaws.getDefaultVertical(
      queryVariables,
      setLHDefaultVertical,
      setLHDefaultVerticalState
    );
  }, [
    weekValue,
    profileState,
    selectedProxyCoachee,
    customDate,
    startDate,
    endDate,
  ]);

  useEffect(() => associateSelectClick(), [weekValue]);

  useEffect(() => {
    if (
      profileState === DataState.LOADING ||
      selectedProxyCoacheeObject.tmEntity.userID === ''
    ) {
      return;
    }

    // Initialize filter Options to Avoid Race Condition
    if (lighthouseFilters !== EmptyFilters) {
      setLighthouseFilters(EmptyFilters);
      setLighthouseFiltersState(DataState.LOADING);
    }

    /**
     * For TM Proxy, GM View is not in the requirement
     * Therefore teamMemberIsTM can default set to equal-sized false array
     * */
    const teamMemberIsTM = new Array(
      selectedProxyCoacheeObject.sharedReports.length
    ).fill(false);

    if (customDate) {
      // custom date range
      const queryVariablesCustomFilters =
        apiNaws.prepareLighthouseCustomFiltersQuery(
          selectedProxyCoacheeObject.tmEntity.userID,
          cscEntityPrefix,
          true,
          startDate,
          endDate,
          selectedProxyCoacheeObject.sharedReports,
          teamMemberIsTM,
          SYNC_METRICS_DEFAULT
        );

      apiNaws.getLighthouseCustomFilters(
        queryVariablesCustomFilters,
        setLighthouseFilters,
        setLighthouseFiltersState
      );
    } else {
      // trailing 7 days or weekly
      const queryVariablesFilters = apiNaws.prepareLighthouseFiltersQuery(
        selectedProxyCoacheeObject.tmEntity.userID,
        cscEntityPrefix,
        true,
        weekValue,
        weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
        selectedProxyCoacheeObject.sharedReports,
        teamMemberIsTM,
        SYNC_METRICS_DEFAULT
      );

      apiNaws.getLighthouseFilters(
        queryVariablesFilters,
        setLighthouseFilters,
        setLighthouseFiltersState
      );
    }
  }, [
    weekValue,
    profileState,
    selectedProxyCoacheeObject,
    syncTimeChangeCount,
    customDate,
    startDate,
    endDate,
  ]);

  // Handle the case for Vertical Options and Default Vertical
  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADING ||
      lighthouseFiltersState === DataState.LOADING
    )
      return;

    if (legacyExperience) {
      setFilterVerticalValue(VerticalOption.Total_CSC);
      return;
    }

    if (
      LHDefaultVertical === null ||
      !lighthouseFilters.verticals.includes(LHDefaultVertical)
    ) {
      setFilterVerticalValue(VerticalOption.Total);
    } else {
      setFilterVerticalValue(convertToVerticalOption(LHDefaultVertical));
    }
  }, [lighthouseFiltersState, LHDefaultVerticalState]);

  useEffect(() => {
    if (
      LHDefaultVerticalState === DataState.LOADING ||
      lighthouseFiltersState === DataState.LOADING
    )
      return;

    if (!LHDefaultVertical) {
      setCheckOptions(false);
      setCheckState(DataState.LOADED);
      return;
    }
    setCheckOptions(lighthouseFilters.verticals.includes(LHDefaultVertical));
    setCheckState(DataState.LOADED);
  }, [lighthouseFiltersState, LHDefaultVerticalState]);

  useEffect(() => {
    if (
      selectedProxyCoachee === '' ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }

    setSkillOptions(lighthouseFilters.skills);
    setChannelOptions(lighthouseFilters.channels);
    setSegmentOptions(lighthouseFilters.segments);
    // ToDo - Remove question marks after the release
    setMarketplaceOptions(lighthouseFilters.marketplaces ?? []);

    /**
     * Display Total as Aggregated - Make new array in the Order, that Total at first index
     */
    const verticalParam = lighthouseFilters.verticals;

    const tempVertical = ['Total', ...verticalParam];
    const sortedVertical = [...new Set(tempVertical)]; // Total is not default vertical - Regard it as Aggregated

    setVerticalOptions(sortedVertical);
  }, [lighthouseFiltersState, LHDefaultVerticalState, selectedProxyCoachee]);

  // Reset check value to false and state to Loaded - since Default Check is used for the first time
  useEffect(() => {
    setCheckOptions(false);
    setCheckState(DataState.LOADED);
  }, [
    verticalValue,
    skillValue,
    channelValue,
    segmentValue,
    marketplaceValue,
    selectedProxyCoacheeObject,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (
      selectedProxyCoacheeObject.tmEntity.userID === '' ||
      verticalValue === '' ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED ||
      checkState === DataState.LOADING
    ) {
      return;
    }

    /**
     * 1. If Default vertical does exist in the filterOptions, then we pass variable in our query
     * 2. If Default vertical doesn't exist in the filterOptions, then we pass Total in our query
     *    2.1 VerticalValue Assignment has been done in another useEffect Block and when it is passed
     *        this block, it has been assigned to 'Total'
     */
    const verticalValueFromDefaultVertical = legacyExperience
      ? VerticalOption['Total_CSC']
      : decideVerticalOption(
          LHDefaultVertical,
          isGoUser,
          enableRAPRemovalAndCCXUpdate
        );

    const verticalValueForCommonCase = legacyExperience
      ? VerticalOption['Total_CSC']
      : convertToVerticalOption(verticalValue);

    let timestamp;
    let timeOption;
    if (customDate) {
      const queryVariables = apiNaws.prepareCustomLighthouseQuery(
        profile.user.userID,
        cscEntityPrefix,
        true,
        checkOptions
          ? verticalValueFromDefaultVertical
          : verticalValueForCommonCase,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        startDate,
        endDate,
        [], // Metrics Array - For topLevels - we pass empty Array
        selectedProxyCoacheeObject.sharedReports,
        SYNC_METRICS_DEFAULT
      );

      apiNaws.getCustomLighthouseMetrics(
        queryVariables,
        setLighthouseMetrics,
        setLighthouseMetricsState
      );
    } else {
      // trailing 7 days or weekly
      timestamp = weekValue;
      timeOption =
        weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY;
      const queryVariables = apiNaws.prepareLighthouseQuery(
        profile.user.userID,
        cscEntityPrefix,
        true,
        checkOptions
          ? verticalValueFromDefaultVertical
          : verticalValueForCommonCase,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        timestamp,
        timeOption,
        [], // Metrics Array - For topLevels - we pass empty Array
        selectedProxyCoacheeObject.sharedReports,
        SYNC_METRICS_DEFAULT
      );

      apiNaws.getLighthouseMetrics(
        queryVariables,
        setLighthouseMetrics,
        setLighthouseMetricsState
      );
    }
    setCheckState(DataState.LOADING);
  }, [
    selectedProxyCoacheeObject,
    verticalValue,
    weekValue,
    skillValue,
    channelValue,
    checkState,
    segmentValue,
    marketplaceValue,
    LHDefaultVerticalState,
    lighthouseFiltersState,
    inspireOptions.state,
    customDate,
    startDate,
    endDate,
  ]);

  // Direct Reports Table - GetTeamLighthouseMetrics
  useEffect(() => {
    if (
      selectedProxyCoacheeObject.tmEntity.userID === '' ||
      verticalValue === '' ||
      checkState === DataState.LOADING ||
      lighthouseFiltersState !== DataState.LOADED ||
      LHDefaultVerticalState !== DataState.LOADED
    ) {
      return;
    }
    const verticalValueFromDefaultVertical = legacyExperience
      ? VerticalOption['Total_CSC']
      : decideVerticalOption(
          LHDefaultVertical,
          isGoUser,
          enableRAPRemovalAndCCXUpdate
        );
    const verticalValueForCommonCase = legacyExperience
      ? VerticalOption['Total_CSC']
      : convertToVerticalOption(verticalValue);

    if (customDate) {
      // date range provided
      const queryVariablesLM = apiNaws.prepareProxyCustomTeamLighthouseQuery(
        selectedProxyCoacheeObject.tmEntity.userID,
        cscEntityPrefix,
        checkOptions
          ? verticalValueFromDefaultVertical
          : verticalValueForCommonCase,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        startDate,
        endDate,
        selectedProxyCoacheeObject.sharedReports
      );
      apiNaws.getCustomTeamLighthouseMetrics(
        queryVariablesLM,
        (payload: LighthouseMetrics[]) => {
          setTeamLHMetrics(payload);
          refreshFilters(
            payload,
            setLatestMetricPublishDate,
            setSyncTimeChangeCount
          );
        },
        setTeamLHMetricsState
      );
    } else {
      // trailing 7 days or weekly
      const queryVariablesLM = apiNaws.prepareProxyTeamLighthouseQuery(
        selectedProxyCoacheeObject.tmEntity.userID,
        cscEntityPrefix,
        checkOptions
          ? verticalValueFromDefaultVertical
          : verticalValueForCommonCase,
        channelValue,
        skillValue,
        segmentValue,
        marketplaceValue,
        weekValue,
        weekValue === TRAILING_DAYS ? TimeOption.DAILY : TimeOption.WEEKLY,
        selectedProxyCoacheeObject.sharedReports
      );
      apiNaws.getTeamLighthouseMetrics(
        queryVariablesLM,
        (payload: LighthouseMetrics[]) => {
          setTeamLHMetrics(payload);
          refreshFilters(
            payload,
            setLatestMetricPublishDate,
            setSyncTimeChangeCount
          );
        },
        setTeamLHMetricsState
      );
    }
  }, [
    selectedProxyCoacheeObject,
    weekValue,
    skillValue,
    channelValue,
    verticalValue,
    marketplaceValue,
    checkState,
    inspireOptions.state,
    LHDefaultVerticalState,
    lighthouseFiltersState,
    customDate,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    if (
      inspireOptions.state !== DataState.LOADED ||
      selectedProxyCoachee === ''
    ) {
      return;
    }
    const queryVariable = { userID: `${selectedProxyCoachee}` };
    apiNaws.getWeeklySummaryCoachingStatus(
      queryVariable,
      setWeeklySummaryCoachingStatus,
      setWeeklySummaryCoachingStatusLoadingState
    );
  }, [inspireOptions.state, selectedProxyCoachee]);

  const [SelectAssociateTrackingTag, associateSelectClick] = useCustomTracking(
    'team-associate-select',
    'team-associate-select-slot',
    {
      'data-csa-c-type': 'select',
      'data-csa-c-selected-associate': selectedAssociate || '',
    }
  );

  // Customer Comments - Set Assoicate
  const setAssociate = (e: React.FormEvent<HTMLSelectElement>): void => {
    setSelectedAssociate(e.toString());
  };

  // Set Select Proxy Coachee with DropDown
  const setProxyCoachee = (e: any): void => {
    const filterProxyCoachee = profile.proxyCoachees.filter(
      coachee => coachee.tmEntity.userID === e
    );
    setSelectedProxyCoacheeObject(filterProxyCoachee[0]);
    setSelectedProxyCoachee(e);
  };

  const setWeek = (e: React.FormEvent<HTMLSelectElement>): void => {
    setWeekValue(e.toString());
  };

  let { startDate: contactStartDate, endDate: contactEndDate } =
    getDateRangeFromWeek(weekValue);

  useEffect(() => {
    const { startDate, endDate } = getDateRangeFromWeek(weekValue);
    contactStartDate = startDate;
    contactEndDate = endDate;
  }, [weekValue]);

  useEffect(() => {
    if (
      profile.user.userID === '' ||
      selectedAssociate === '' ||
      !selectedAssociate
    )
      return;
    if (weekValue && enableCesComments) {
      apiNaws.getCustomerCommentsData(
        apiNaws.prepareQueryForCustomerComment({
          selectedWeek: weekValue,
          userID: selectedAssociate,
          cscEntityPrefix,
          enableProfanityFilter,
        }),
        setCustomerComments,
        setCustomerCommentsState
      );
      setContactHandlerId(`${cscEntityPrefix}:${selectedAssociate}`);
    }
  }, [weekValue, selectedAssociate, profile.user.userID, inspireOptions.state]);

  const weekValueChanger = (week: any) => {
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
    setCustomDate(false);
    setDateRange(defaultDateRange);
    legacyExperience
      ? setFilterVerticalValue(VerticalOption.Total_CSC)
      : LHDefaultVertical === null
        ? setFilterVerticalValue(VerticalOption.Total)
        : setFilterVerticalValue(LHDefaultVertical);
    setWeek(week);
  };

  useEffect(() => {
    setSkillValue('ALL');
    setChannelValue('ALL');
    setSegmentValue('ALL');
    setMarketplaceValue('ALL');
  }, [verticalValue]);

  useEffect(() => {
    let filterBar = document.getElementById(FILTER_BAR_ID);
    if (
      filterBar?.offsetHeight &&
      filterBar?.offsetHeight > FILTER_BAR_HEIGHT_THRESHOLD
    ) {
      // two line filter bar
      setShortFilterBy(true);
    } else {
      // one line filter bar
      setShortFilterBy(false);
    }
  }, [customDate, verticalValue]);

  const teamMetricTypes = useMemo(
    () =>
      new Set(teamLHMetrics.map(metrics => metrics.metricTypes ?? []).flat()),
    [teamLHMetrics]
  );

  return (
    <Box>
      <Responsive
        query='min-width'
        props={{
          isBig: {
            default: false,
            '1080px': true,
          },
        }}
      >
        {({ isBig }): React.ReactElement => (
          <>
            {teamNotificationEnabled && teamNotifContent !== null && (
              <Column spacing='none 400 500'>
                <Box spacingInset='none none 500'>
                  <Alert>
                    <Text>
                      {teamNotifContent?.defaultContent}{' '}
                      {teamNotifContent?.linkHref !== undefined &&
                        teamNotifContent?.linkText !== undefined && (
                          <Link
                            href={teamNotifContent?.linkHref}
                            target='_blank'
                            data-csa-c-type='link'
                            data-csa-c-slot-id='team-notification-link-slot'
                            data-csa-c-content-id='team-notification-link'
                          >
                            {teamNotifContent?.linkText}
                          </Link>
                        )}
                    </Text>
                  </Alert>
                </Box>
              </Column>
            )}

            {teamAlertEnabled && teamAlertContent !== null && (
              <Column spacing='none 400 500'>
                <Box spacingInset='none none 500'>
                  <Alert type='warning'>
                    <Text>{teamAlertContent}</Text>
                  </Alert>
                </Box>
              </Column>
            )}

            {!newCoachingEnabled && (
              <Box spacingInset='none none 500'>
                <Alert>
                  <Text>
                    <FormattedMessage
                      id='cs_perfportal_italy_release_banner'
                      defaultMessage='You do not have access to the coaching form at the moment. You will have access to all features within Inspire as soon as we have the necessary regulatory approvals to launch.'
                    />
                  </Text>
                </Alert>
              </Box>
            )}

            {profileState === DataState.LOADING && (
              <div style={{ textAlign: 'center' }}>
                <Loader type='circular' />
              </div>
            )}

            {profileState === DataState.LOADED && (
              <TeamProfileContainer
                state={profileState}
                profile={profile}
                selectedWeek={weekValue}
                setWeek={setWeek}
                isBig={isBig}
              />
            )}

            {profileState === DataState.ERROR && (
              <Alert type='error'>
                <FormattedMessage
                  id='cs_perfportal_error_reason_1'
                  defaultMessage='Your credentials may be incorrect or inadequate.'
                />
              </Alert>
            )}

            <Row
              spacingInset='none none 400'
              spacing='400'
              widths={['75.87%', '24.13%']}
            >
              <Box>
                <Heading level={1} type='h400'>
                  <FormattedMessage
                    id='cs_perfportal_metric_proxyteam_performance'
                    defaultMessage='Proxy Team Performance'
                  />
                </Heading>
              </Box>
              <Box>
                <Select
                  value={selectedProxyCoachee}
                  onChange={setProxyCoachee}
                  size='medium'
                >
                  {proxyCoachees.map(proxyCoachee => (
                    <SelectOption
                      key={proxyCoachee.tmEntity.userID}
                      label={proxyCoachee.tmEntity.displayName}
                      value={proxyCoachee.tmEntity.userID}
                      data-csa-c-type='select'
                      data-csa-c-slot-id='proxy-tab-proxy-coachee-select-field-slot'
                      data-csa-c-content-id='proxy-tab-proxy-coachee-select-field'
                      data-csa-c-selected-value={proxyCoachee.tmEntity.userID}
                    >
                      {(): React.ReactElement => (
                        <>
                          <Row spacing='none' spacingInset='none'>
                            <Box width='34.9px' height='36px'>
                              <UserBadge
                                username={proxyCoachee.tmEntity.userName}
                                givenName={proxyCoachee.tmEntity.displayName}
                                tooltip
                              />
                            </Box>
                            <Text>{proxyCoachee.tmEntity.displayName}</Text>
                          </Row>
                        </>
                      )}
                    </SelectOption>
                  ))}
                </Select>
              </Box>
            </Row>

            <Box type='fill' spacingInset='400'>
              {(lighthouseFiltersState === DataState.LOADING ||
                LHDefaultVerticalState === DataState.LOADING) && (
                <div style={{ textAlign: 'center' }}>
                  <Loader type='circular' size='large' />
                </div>
              )}
              {lighthouseFiltersState === DataState.LOADED &&
                LHDefaultVerticalState === DataState.LOADED && (
                  <Row
                    width='100%'
                    wrap='down'
                    alignmentVertical='center'
                    alignmentHorizontal='justify'
                  >
                    <div id={FILTER_BAR_ID}>
                      <Row wrap='down' alignmentVertical='top'>
                        <LighthouseFilterBy
                          shortFilterBy={shortFilterBy}
                          customDate={customDate}
                          page={Page.TEAM}
                        />
                        <Filter
                          verticalValue={filterVerticalValue}
                          weekValue={weekValue}
                          skillValue={skillValue}
                          channelValue={channelValue}
                          verticalOptions={verticalOptions}
                          skillOptions={skillOptions}
                          channelOptions={channelOptions}
                          filterTruncate={filterTruncate}
                          weekOptions={weekOptions}
                          customDate={customDate}
                          dateRange={dateRange}
                          setVerticalValue={setFilterVerticalValue}
                          setSkillValue={setSkillValue}
                          setChannelValue={setChannelValue}
                          weekValueChanger={weekValueChanger}
                          segmentOptions={segmentOptions}
                          segmentValue={segmentValue}
                          setSegmentValue={setSegmentValue}
                          setCustomDate={setCustomDate}
                          setDateRange={setDateRange}
                          page={Page.TEAM}
                          marketplaceValue={marketplaceValue}
                          marketplaceOptions={marketplaceOptions}
                          setMarketplaceValue={setMarketplaceValue}
                        />
                      </Row>
                    </div>
                  </Row>
                )}
              {(lighthouseFiltersState === DataState.ERROR ||
                LHDefaultVerticalState === DataState.ERROR) && (
                <Alert type='error'>
                  <FormattedMessage
                    id='cs_perfportal_error_refresh_page'
                    defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                  />
                </Alert>
              )}
            </Box>

            {(teamState === DataState.LOADING ||
              lighthouseFiltersState === DataState.LOADING ||
              LHDefaultVerticalState === DataState.LOADING ||
              lighthouseMetricsState === DataState.LOADING) && (
              <div style={{ textAlign: 'center' }}>
                <Loader type='circular' />
              </div>
            )}

            {teamState === DataState.LOADED &&
              lighthouseFiltersState === DataState.LOADED &&
              LHDefaultVerticalState === DataState.LOADED &&
              lighthouseMetricsState === DataState.LOADED && (
                <Row width='100%' spacingInset='none' spacing='none'>
                  <Summary
                    keys={team.keys}
                    vertical={verticalValue}
                    lighthouseMetrics={lighthouseMetrics}
                    achtEnabled={achtEnabled}
                    concessionEnabled={concessionEnabled}
                    manageCoachingNotes={manageCoachingNotes}
                    teamMetricTypes={teamMetricTypes}
                  />
                </Row>
              )}
            {(teamState === DataState.ERROR ||
              lighthouseFiltersState === DataState.ERROR ||
              LHDefaultVerticalState === DataState.ERROR ||
              lighthouseMetricsState === DataState.ERROR) && (
              <>
                <Row width='100%' spacingInset='400 none none' spacing='none'>
                  {!selectedProxyCoacheeObject.sharedReports.length ? (
                    <Text>
                      <FormattedMessage
                        id='cs_perfportal_no_associates_found'
                        defaultMessage='No associates found.'
                      />
                    </Text>
                  ) : (
                    <Alert type='error'>
                      <FormattedMessage
                        id='cs_perfportal_error_refresh_page'
                        defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                      />
                    </Alert>
                  )}
                </Row>
              </>
            )}

            {(teamState === DataState.LOADING ||
              LHDefaultVerticalState === DataState.LOADING ||
              teamLHMetricsState === DataState.LOADING ||
              lighthouseFiltersState === DataState.LOADING) && (
              <div style={{ textAlign: 'center' }}>
                <Loader type='circular' />
              </div>
            )}
            {teamState === DataState.LOADED &&
              lighthouseFiltersState === DataState.LOADED &&
              LHDefaultVerticalState === DataState.LOADED &&
              teamLHMetricsState === DataState.LOADED && (
                <>
                  <Box type='fill' spacingInset='400'>
                    <Row
                      width='100%'
                      wrap='down'
                      alignmentVertical='center'
                      alignmentHorizontal='justify'
                    >
                      <Row wrap='down'>
                        <Text>
                          <FormattedMessage
                            id='cs_perfportal_sort_metric_by'
                            defaultMessage='Sort Metric by'
                          />
                        </Text>
                        <Sort
                          sortMethod={sortingMethod}
                          setSortMethod={setSortingMethod}
                          sourcePage={Page.TEAM_PROXY}
                        />
                      </Row>
                    </Row>
                  </Box>
                  <Row width='100%' spacingInset='none' spacing='none'>
                    <DirectReports
                      cscEntityPrefix={cscEntityPrefix}
                      vertical={convertToVerticalOption(verticalValue)}
                      channelValue={channelValue}
                      skillValue={skillValue}
                      segmentValue={segmentValue}
                      marketplaceValue={marketplaceValue}
                      keys={team.keys}
                      week={weekValue}
                      profile={profile}
                      weeklySummaryCoachingStatus={weeklySummaryCoachingStatus}
                      showWeeklyCoachingStatus
                      teamLHMetrics={teamLHMetrics}
                      achtEnabled={achtEnabled}
                      concessionEnabled={concessionEnabled}
                      manageCoachingNotes={manageCoachingNotes}
                      proxyCoachID={selectedProxyCoachee}
                      sortingMethod={sortingMethod}
                      sourcePage={Page.TEAM_PROXY}
                      teamMetricTypes={teamMetricTypes}
                    />
                  </Row>
                </>
              )}

            {(teamState === DataState.ERROR ||
              lighthouseFiltersState === DataState.ERROR ||
              LHDefaultVerticalState === DataState.ERROR ||
              teamLHMetricsState === DataState.ERROR) && (
              <Row width='100%' spacingInset='none' spacing='none'>
                <Alert type='error'>
                  <FormattedMessage
                    id='cs_perfportal_error_refresh_page'
                    defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
                  />
                </Alert>
              </Row>
            )}

            {enableCesComments && (
              <>
                <Row
                  spacingInset='500 none none'
                  spacing='400'
                  widths={['75.87%', '24.13%']}
                >
                  <Box>
                    <Heading level={1} type='h400'>
                      <FormattedMessage
                        id='cs_perfportal_customerComments'
                        defaultMessage='Customer comments'
                        description='header of Customer Comments Table'
                      />
                    </Heading>
                  </Box>
                  <Box>
                    <SelectAssociateTrackingTag />
                    <Select
                      value={selectedAssociate}
                      onChange={setAssociate}
                      size='medium'
                    >
                      {associateList.map(associate => {
                        return (
                          <SelectOption
                            key={associate.userID}
                            value={associate.userID}
                            label={associate.displayName}
                            data-csa-c-type='select'
                            data-csa-c-slot-id='lighthouse-filters-select-username-customer-comments'
                            data-csa-c-content-id='lighthouse-filters-select-username-customer-comments'
                            data-csa-c-selected-value={associate.userID}
                            data-csa-c-tag1='Team'
                            data-csa-c-tag2='Customer Comments'
                          >
                            {(): React.ReactElement => (
                              <>
                                <Row spacing='none' spacingInset='none'>
                                  <Box width='34.9px' height='36px'>
                                    <UserBadge
                                      username={associate.userName}
                                      givenName={associate.displayName}
                                      tooltip
                                    />
                                  </Box>
                                  <Text>{associate.displayName}</Text>
                                </Row>
                              </>
                            )}
                          </SelectOption>
                        );
                      })}
                    </Select>
                  </Box>
                </Row>

                {customerCommentsState === DataState.LOADING && (
                  <div style={{ textAlign: 'center' }}>
                    <Loader type='circular' size='large' />
                  </div>
                )}

                {customerCommentsState === DataState.LOADED && (
                  <Row spacingInset='none' spacing='none'>
                    <CustomerComments
                      customerMessages={customerComments}
                      page={Page.TEAM}
                      contactStartDate={contactStartDate}
                      contactEndDate={contactEndDate}
                      contactHandlerId={contactHandlerId}
                    />
                  </Row>
                )}
                {customerCommentsState === DataState.ERROR && (
                  <Alert type='error'>
                    <FormattedMessage
                      id='cs_perfportal_error_reason_1'
                      defaultMessage='Your credentials may be incorrect or inadequate.'
                    />
                  </Alert>
                )}
              </>
            )}
          </>
        )}
      </Responsive>
    </Box>
  );
};

export default ProxyTeam;
